"use client";

import { api } from "@/convex/_generated/api";
import { useQuery } from "convex/react";
import { useRollout } from "@/app/_hooks/use-rollout";
import { useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/app/v2/_components/button/button";
import { Check, Link2, Loader2, RefreshCcw, TriangleAlert, Unlink, Users, Tags, RotateCcw } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Link from "next/link";
import Image from "next/image";
import { v4 as uuidv4 } from "uuid";
import { withUser } from "@/hooks/with-user";
import { Row } from "@/app/v2/_components/row/Row";
import { Text } from "@/app/v2/_components/text/Text";
import { format, formatDistanceToNow } from "date-fns";
import { Column } from "@/app/v2/_components/column/Column";
import { Contacts } from "@/app/v2/_components/contacts/contacts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

interface ConnectedCrmStatusProps {
  onReconnect?: () => void;
}

export function ConnectedCrmStatus({ onReconnect }: ConnectedCrmStatusProps) {
  const { team } = withUser();
  const {
    crmConfig,
    crmFriendlyName,
    crmMetadata,
    dataSummary,
    deleteCredential,
    isLoadingData,
    isLoadingTags,
    refreshStatus,
    tags,
  } = useRollout();

  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const [showContacts, setShowContacts] = useState(false);
  const [showTags, setShowTags] = useState(false);

  const latestRun = useQuery(
    api.bucketer.queries.getLatestRun,
    team?._id ? { teamId: team._id } : "skip"
  );

  const { isFub, isRollout, isSyncing, isConnected } = crmMetadata ?? {};

  // Out of sync: the FUB grant died but the team stays connected — existing
  // contacts keep working, only syncing is paused until the user reconnects.
  const isOutOfSync = Boolean(isFub && team?.crm?.status === "connected" && team?.crm?.authFailedAt != null);

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      const result = await deleteCredential();
      if (!result.success) {
        toast.error(result.errorMessage ?? "An error occurred while disconnecting");
        return;
      }
      setShowDisconnectConfirm(false);
      toast.success("CRM disconnected successfully");
    } catch (err) {
      console.error("Error disconnecting:", err);
      toast.error("An error occurred while disconnecting");
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleReconnect = useCallback(() => {
    if (isFub) {
      // Redirect to FUB OAuth
      const oauthUrl = `https://app.followupboss.com/oauth/authorize?response_type=auth_code&client_id=${process.env.NEXT_PUBLIC_FUB_CLIENT_ID
        }&redirect_uri=${process.env.NEXT_PUBLIC_FUB_REDIRECT_URI!}&state=${uuidv4()}`;
      window.location.href = oauthUrl;
    } else {
      onReconnect?.();
    }
  }, [isFub, onReconnect]);

  if (!isConnected) {
    return null;
  }

  // Render CRM logo or icon
  const renderCrmIcon = () => {
    if (crmConfig?.logoUrl) {
      return (
        <Image
          src={crmConfig.logoUrl ?? ""}
          alt={crmFriendlyName ?? ""}
          width={36}
          height={36}
          className="rounded-lg h-9 w-9"
        />
      );
    }

    if (crmConfig?.bgColor && crmConfig?.icon) {
      return (
        <div className={`w-12 h-12 ${crmConfig.bgColor} rounded-lg flex items-center justify-center`}>
          <span className="text-white text-xl font-bold">{crmConfig.icon}</span>
        </div>
      );
    }

    return (
      <div className="w-12 h-12 bg-zinc-200 rounded-lg flex items-center justify-center">
        <Link2 className="h-6 w-6 text-zinc-500" />
      </div>
    );
  };

  return (
    <TooltipProvider>
      <Column bigGap className="w-full p-4 border rounded-md max-w-5xl bg-muted/30">
        <Column>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 w-full">
              {renderCrmIcon()}
              <Column noGap wide>
                <Row align between>
                  <Column noGap>
                    <Text lg chonky className="flex items-center gap-2 capitalize">
                      {crmFriendlyName === "unknown" ? "CRM" : crmFriendlyName}
                    </Text>
                    {team.crm?.lastSyncedAt && (
                      <Text sm subdued>
                        Last synced {formatDistanceToNow(new Date(team.crm.lastSyncedAt), { addSuffix: true })}
                      </Text>
                    )}
                  </Column>
                  {isSyncing ? (
                    <Row align className="text-blue-600" tightGap shrink>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <Text sm>Syncing...</Text>
                    </Row>
                  ) : isOutOfSync ? (
                    <Row align className="text-amber-600" tightGap shrink>
                      <TriangleAlert className="h-4 w-4" />
                      <Text sm>Out of sync</Text>
                    </Row>
                  ) : (
                    <Row align className="text-teal-500" tightGap shrink>
                      <Check className="h-4 w-4" />
                      <Text sm>Connected</Text>
                    </Row>
                  )}
                </Row>

                {dataSummary?.lastSyncedAt && dataSummary?.lastSyncedAt > 0 && (
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger className="flex items-start w-fit">
                      <Text subdued>
                        Last synced{" "}
                        {formatDistanceToNow(new Date(dataSummary?.lastSyncedAt || 0), { addSuffix: true })}{" "}
                      </Text>
                    </TooltipTrigger>
                    <TooltipContent className="p-4">
                      <Row tightGap align>
                        <RotateCcw className="h-5 w-5" />
                        <Text>{format(new Date(dataSummary?.lastSyncedAt || 0), "MMM d, yyyy 'at' h:mm aa")}</Text>
                      </Row>
                    </TooltipContent>
                  </Tooltip>
                )}
              </Column>
            </div>
          </div>
        </Column>

        <Column className="space-y-6">
          {/* Out of sync: reconnect nudge */}
          {isOutOfSync && !isSyncing && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <Row align className="gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <TriangleAlert className="w-5 h-5 text-amber-600" />
                </div>
                <Column noGap wide>
                  <Text className="font-medium text-amber-900">Your Follow Up Boss connection stopped syncing</Text>
                  <Text sm className="text-amber-800">
                    Your contacts and campaigns keep working with the last synced data. Reconnect to get back in sync.
                  </Text>
                </Column>
                <Button variant="outline" className="shrink-0 text-amber-900" onClick={handleReconnect}>
                  <Row align tightGap>
                    <RefreshCcw className="h-4 w-4" />
                    Reconnect
                  </Row>
                </Button>
              </Row>
            </div>
          )}

          {/* Sync in progress message */}
          {isSyncing && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <Row align className="gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                </div>
                <Column noGap>
                  <Text className="font-medium text-blue-900">Syncing your contacts...</Text>
                  <Text sm className="text-blue-700">
                    This may take a few minutes. You can leave this page and we'll continue in the background.
                  </Text>
                </Column>
              </Row>
            </div>
          )}

          {latestRun?.status === "complete" ? (
            <Column>
              <Row align>
                {/* Team Members */}
                <Link href="/v2/team" className="w-full">
                  <Button variant="default" className="w-full" asChild>
                    <Row align>
                      <div className="p-2.5 bg-orange-100 rounded-lg shrink-0">
                        <Users className="h-5 w-5 text-orange-600" />
                      </div>
                      <Row align between>
                        <Text sm subdued>
                          Team Members
                        </Text>
                        <Text lg chonky>
                          {isLoadingData ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            (dataSummary?.teamMemberCount?.toLocaleString() ?? "—")
                          )}
                        </Text>
                      </Row>
                    </Row>
                  </Button>
                </Link>
              </Row>
            </Column>
          ) : (
            isRollout &&
            !isSyncing && (
              <Column>
                <Row align>
                  {/* Team Members */}
                  <Link href="/v2/settings/team" className="w-full">
                    <Button variant="outline" className="w-full" asChild>
                      <Row align>
                        <div className="p-2.5 bg-orange-100 rounded-lg shrink-0">
                          <Users className="h-5 w-5 text-orange-600" />
                        </div>
                        <Row align between>
                          <Text sm subdued>
                            Team Members
                          </Text>
                          <Text lg chonky>
                            {isLoadingData ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              (dataSummary?.teamMemberCount?.toLocaleString() ?? "—")
                            )}
                          </Text>
                        </Row>
                      </Row>
                    </Button>
                  </Link>

                  {/* Contacts */}
                  <Button variant="outline" className="w-full" onClick={() => setShowContacts(true)}>
                    <Row align>
                      <div className="p-2.5 bg-blue-100 rounded-lg shrink-0">
                        <Tags className="h-5 w-5 text-blue-600" />
                      </div>
                      <Row align between>
                        <Text sm subdued>
                          Contacts
                        </Text>
                        <Text lg chonky>
                          {isLoadingData ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            (dataSummary?.contactCount?.toLocaleString() ?? "—")
                          )}
                        </Text>
                      </Row>
                    </Row>
                  </Button>

                  {/* Tags/Lists */}
                  <Button variant="outline" className="w-full" onClick={() => setShowTags(true)}>
                    <Row align>
                      <div className="p-2.5 bg-purple-100 rounded-lg shrink-0">
                        <Tags className="h-5 w-5 text-purple-600" />
                      </div>
                      <Row align between>
                        <Text sm subdued>
                          Tags
                        </Text>
                        <Text lg chonky>
                          {isLoadingData ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            (dataSummary?.tagCount?.toLocaleString() ?? "—")
                          )}
                        </Text>
                      </Row>
                    </Row>
                  </Button>
                </Row>
              </Column>
            )
          )}

          {/* Actions */}
          <Row align className="pt-4">
            <Button variant="outline" size="sm" onClick={handleReconnect}>
              <RefreshCcw className="h-4 w-4 mr-2" />
              Reconnect
            </Button>

            <Button variant="outline" size="sm" onClick={() => setShowDisconnectConfirm(true)}>
              <Unlink className="h-4 w-4 mr-2" />
              Disconnect
            </Button>

            {isRollout && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => refreshStatus()}
                disabled={isLoadingData}
                className="ml-auto text-muted-foreground"
              >
                {isLoadingData ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCcw className="h-4 w-4 mr-2" />
                )}
                Refresh Data
              </Button>
            )}
          </Row>
        </Column>
      </Column>

      {/* Disconnect Confirmation */}
      <AlertDialog open={showDisconnectConfirm} onOpenChange={setShowDisconnectConfirm}>
        <AlertDialogContent>
          <AlertDialogTitle className="sr-only">Disconnect {crmFriendlyName}?</AlertDialogTitle>
          <AlertDialogHeader>
            <Text lg chonky>
              Disconnect {crmFriendlyName}?
            </Text>
            <Text sm subdued>
              This will remove your CRM connection. Your synced contacts will remain, but new contacts won't be synced
              until you reconnect.
            </Text>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel size="sm" disabled={isDisconnecting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              size="sm"
              className="bg-rose-100 hover:bg-rose-200 border border-rose-600/20 text-rose-600"
            >
              {isDisconnecting ? <Loader2 className="animate-spin" /> : <Unlink />}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={showContacts} onOpenChange={setShowContacts} modal>
        <DialogContent className="bg-white min-h-[80vh] h-[80vh] min-w-[99vh] w-[90vw] overflow-y-auto">
          <DialogHeader className="sr-only">
            <DialogTitle className="sr-only">Contacts</DialogTitle>
          </DialogHeader>
          <Contacts />
        </DialogContent>
      </Dialog>

      {/* Tags Dialog */}
      <Dialog open={showTags} onOpenChange={setShowTags}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tags className="h-5 w-5 text-purple-600" />
              Tags
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {isLoadingTags ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : tags.length === 0 ? (
              <Text subdued className="text-center py-8">
                No tags found
              </Text>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {tags.map((tag) => (
                  <div
                    key={tag.tag}
                    className="flex items-center justify-between p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors"
                  >
                    <Text sm className="font-medium">
                      {tag.name}
                    </Text>
                    <Text sm subdued>
                      {tag.memberCount.toLocaleString()} contacts
                    </Text>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
